<template>
    <div class="section-wrapper">
        <!-- Reason Dropdown -->
        <dropdown-generic
        id="internationalTransferReasonLeafId"
        class="dropdown-container"
        :params="transferReasonsParams"
        :options="parsedTransferReasonsList"
        :selectedOption="formData.internationalTransferReasonLeafId"
        @clicked="reasonChangeHandler"/>
        <!-- Operation Type Dropdown -->
        <dropdown-generic
        v-if="!!getOperationTypesList.length && formData.internationalTransferReasonLeafId"
        id="internationalTransferOperationTypeId"
        class="dropdown-container"
        :key="formData.internationalTransferOperationTypeId"
        :params="transferOperationTypeParams"
        :options="parsedTransferOperationTypes"
        :selectedOption="formData.internationalTransferOperationTypeId"
        @clicked="operationTypeChangeHandler"/>
        <!-- Operation Category Dropdown -->
        <dropdown-generic
        v-if="!!getCategoriesList.length && formData.internationalTransferOperationTypeId && getCategoriesList.length > 1"
        id="internationalTransferPaymentCategoryId"
        class="dropdown-container"
        :params="operationCategoryParams"
        :options="parsedOperationCategory"
        :selectedOption="formData.internationalTransferPaymentCategoryId"
        @clicked="operationCategoryChangeHandler"/>
        <!-- Operation Category Text Block -->
        <text-blocks-generic
        v-if="!!getCategoriesList.length && formData.internationalTransferOperationTypeId && getCategoriesList.length === 1 && getCategoriesList.find(obj => obj.id === formData.internationalTransferPaymentCategoryId)"
        :params="operationCategoryBlockParams"/>
        <!-- Expenses Dropdown -->
        <dropdown-generic
        id="internationalTransferExpenseId"
        class="dropdown-container"
        :key="formData.internationalTransferExpenseId"
        :params="transferExpensesParams"
        :options="parsedTransferExpense"
        :selectedOption="formData.internationalTransferExpenseId"
        @clicked="[
            emit('updateFormData', { field: 'internationalTransferExpenseId', inputValue: $event }),
            clearFieldError('internationalTransferExpenseId')
        ]"/>
    </div>
</template>

<script setup>
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import TextBlocksGeneric from '@/components/generic/textBlocks/Generic'

/* Vue && Assets */
import { ref, computed, watch } from 'vue'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getTransfersReasons, getOperationTypesList, getCategoriesList, getTransfersExpenses } = mapGetters(moduleMapper.TRANSFERS_INSTANT_BAI)

/** Props
@formData {Object} - Object with form data
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@clearForm {Boolean} - Boolean controller to clear form and errors
@fieldsWithError {Array} - Input List with error MDW provides to use
*/
const props = defineProps({
    formData: Object,
    showInputsErrors: Boolean,
    clearForm: Boolean,
    fieldsWithError: Array
})

const emit = defineEmits([
    'updateFormData',
    'requestOperationsTypes',
    'requestCategories',
    'resetShowInputsErrors',
    'formIsCleared',
    'scrollTo'
])

/*************************************************
*                                                *
*               Transfer Reasons                 *
*                                                *
*************************************************/
const transferReasonsParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.transferReasons'),
    placeholder: t('transfers.instantBAI.genericLabels.transferReasonsPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedTransferReasonsList = computed(() => {
    return getTransfersReasons.value.map(reason => ({
        ...reason,
        id: reason.id,
        text: reason.description
    }))
})

const reasonChangeHandler = event => {
    if (event === props.formData.internationalTransferReasonLeafId) return

    emit('updateFormData', { field: 'internationalTransferReasonLeafId', inputValue: event })

    clearFormErrors()

    emit('requestOperationsTypes')
}

/*************************************************
*                                                *
*                Operation Types                 *
*                                                *
*************************************************/
const transferOperationTypeParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.operationType'),
    placeholder: t('transfers.instantBAI.step1.placeholders.operationType'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedTransferOperationTypes = computed(() => {
    return getOperationTypesList.value.map(type => ({
        ...type,
        id: type.id,
        text: type.description
    }))
})

const operationTypeChangeHandler = event => {
    if (event === props.formData.internationalTransferOperationTypeId) return

    emit('updateFormData', { field: 'internationalTransferOperationTypeId', inputValue: event })

    clearFormErrors()

    emit('requestCategories')
}

/*************************************************
*                                                *
*              Operation Categories              *
*                                                *
*************************************************/
const operationCategoryParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.operationCategory'),
    placeholder: t('transfers.instantBAI.step1.placeholders.operationCategory'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedOperationCategory = computed(() => {
    return getCategoriesList.value.map(type => ({
        ...type,
        id: type.id,
        text: type.description
    }))
})

const operationCategoryBlockParams = computed(() => {
    return {
            label: t('transfers.instantBAI.genericLabels.operationCategory'),
            content: getCategoriesList.value.find(category => category.id === props.formData.internationalTransferPaymentCategoryId).description
        }
})

const operationCategoryChangeHandler = event => {
    if (event === props.formData.internationalTransferPaymentCategoryId) return

    emit('updateFormData', { field: 'internationalTransferPaymentCategoryId', inputValue: event })

    clearFormErrors()
}

/*************************************************
*                                                *
*                Transfer Expense                *
*                                                *
*************************************************/
const transferExpensesParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.transferExpense'),
    placeholder: t('transfers.instantBAI.step1.placeholders.transferExpense'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedTransferExpense = computed(() => {
    return getTransfersExpenses.value.map(type => ({
        ...type,
        id: type.id,
        text: type.description
    }))
})

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/

const operationsMandatoryList = ref([
    'internationalTransferReasonLeafId',
    'internationalTransferOperationTypeId',
    'internationalTransferPaymentCategoryId',
    'internationalTransferExpenseId'
])

const fieldsWithErrorMessage = ref({
    internationalTransferReasonLeafId: transferReasonsParams.value,
    internationalTransferOperationTypeId: transferOperationTypeParams.value,
    internationalTransferPaymentCategoryId: operationCategoryParams.value,
    internationalTransferExpenseId: transferExpensesParams.value
})

const showInputsErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, operationsMandatoryList.value)

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            fieldsWithErrorMessage.value[fieldName].errorMessage = t('mandatoryFieldError')
            fieldsWithErrorMessage.value[fieldName].errorState = true
        })

        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))
    }

    if (props.fieldsWithError.length) {
        props.fieldsWithError.forEach(field => {
            if (fieldsWithErrorMessage.value[field.parsedFieldOnError]) {
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorState = true
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorMessage = field.errorMessage
            }
        })

        emit('scrollTo', getElementOffsetTopPosition(props.fieldsWithError[0]))
    }
}

watch(() => props.showInputsErrors, newVal => {
	if (newVal) showInputsErrorsHandler()
	emit('resetShowInputsErrors')
})

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/
const clearFieldError = (fieldName) => {
    fieldsWithErrorMessage.value[fieldName].errorMessage = null
    fieldsWithErrorMessage.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in fieldsWithErrorMessage.value) {
		clearFieldError(fieldName)
	}
}

watch(() => props.clearForm, () => {
	clearFormErrors()
	emit('formIsCleared')
})
</script>

<style lang="scss" scoped>
.section-wrapper {
    width: 100%;
    gap: 24px;

    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
}
</style>
